import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';

const ENDPOINT_URL = '/adm-complaint-categories/categories';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export interface GetComplaintCategorySuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get all complaint categories.
 */
export const useGetComplaintCategories = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setComplaintCategories } = useData();

    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const request = async () => {
        setLoading(true);
        setSuccess(null);
        setErrorMessage(null);
        setComplaintCategories(null);

        try {
             const commonParams = getCommonParameters();
            const queryString = new URLSearchParams(commonParams).toString();
            const fullUrl = queryString ? `${API_URL}${ENDPOINT_URL}?${queryString}` : `${API_URL}${ENDPOINT_URL}`;

            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;

                throw error;
            }

            const data = await response.json();

            setSuccess(data.message);
            setComplaintCategories(data as GetComplaintCategorySuccessResponse);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
        } finally {
            setLoading(false);
        }
    };

    return { request, loading, success, errorMessage };
};
